import React, { useEffect, useState } from 'react';
import InviteItem from './InviteItem';
import EmptyInvitesList from './EmptyInvitesList';
import { InvitationModel } from 'services/EpdClient';
import styled from 'styled-components';
import MembershipService from 'services/MembershipService';
import LoadingSkeleton from '../LoadingSkeleton';
import { toaster } from 'components/v2/toast';
import { confirmAlert } from 'components/v2/confirm-dialog/confirmAlert';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { useMutation } from '@tanstack/react-query';
import AuthService from 'services/AuthService';

const UserSettingsInvitesTab: React.FC = () => {
  const [invitations, setInvitations] = useState<InvitationModel[] | null>(null);

  const fetchInvitations = async () => {
    try {
      const result = await MembershipService.getInvitationsByUser();
      setInvitations(result ?? []);
    } catch (error) {
      toaster({
        severity: 'error',
        summary: 'Something went wrong.',
        details: 'Please try again later or contact support.',
      });
    }
  };

  useEffect(() => {
    fetchInvitations();
  }, []);

  const acceptMutation = useMutation({
    mutationFn: (inviteId: string) => MembershipService.approveInvitation(inviteId),
    onSuccess: async () => {
      await fetchInvitations();
      await AuthService.refreshUser();
      toaster({ severity: 'success', summary: 'Operation completed successfully!' });
    },
    onError: () => {
      toaster({
        severity: 'error',
        summary: 'Something went wrong.',
        details: 'Please try again later or contact support.',
      });
    },
  });

  const declineMutation = useMutation({
    mutationFn: (inviteId: string) => MembershipService.rejectInvitation(inviteId),
    onSuccess: async () => {
      await fetchInvitations();
      toaster({ severity: 'success', summary: 'Operation completed successfully!' });
    },
    onError: () => {
      toaster({
        severity: 'error',
        summary: 'Something went wrong.',
        details: 'Please try again later or contact support.',
      });
    },
  });

  const handleAccept = (inviteId: string) => acceptMutation.mutate(inviteId);

  const handleDecline = (inviteId: string, companyName?: string) => {
    confirmAlert({
      caption: `Do you want to decline the invite from ${companyName}?`,
      description: <div>Declining means you will not be able to join this organisation unless you are invited again.</div>,
      acceptLabel: 'Confirm',
      rejectLabel: 'Cancel',
      onConfirm: () => declineMutation.mutate(inviteId),
    });
  };

  const isLoading = acceptMutation.isPending || declineMutation.isPending;

  if (invitations === null || isLoading) return <LoadingSkeleton />;
  if (invitations.length === 0) return <EmptyInvitesList />;

  return (
    <>
      <ConfirmDialog />
      <InfoBlock>Here you can find the invites you have received to join organisations.</InfoBlock>
      <InvitesList>
        {invitations.map((invitation) => (
          <InviteItem
            key={invitation.id}
            id={invitation.id}
            companyName={invitation.companyName}
            createdAt={invitation.createdAt}
            onAccept={handleAccept}
            onDecline={handleDecline}
          />
        ))}
      </InvitesList>
    </>
  );
};

const InfoBlock = styled.div`
  width: 100%;
  height: 52px;
  background-color: #b4d0b64d;
  border-radius: 4px;
  padding: 16px 0 16px 16px;
  font-family: Inter;
  font-weight: 400;
  font-size: 14px;
`;

const InvitesList = styled.div`
  margin-top: 16px;
`;

export default UserSettingsInvitesTab;
